import type { Workout } from '@jackedlogs/shared';

// Per-exercise recency for the Add-Exercise picker's "ultimi loggati per
// primi" ordering (Antenore, inbox 963ca6f2 §2). Pure: the host passes its
// recent-sessions window (mobile: the cached window useEntryHistory primes)
// into buildRecencyMap and injects the result; the picker sorts with
// compareByRecency. NO extra network call.

// slug → most recent session startedAt (ISO). A slug not in the window is
// simply absent → sorts after everything that has been logged recently.
export function buildRecencyMap(
  workouts: ReadonlyArray<Workout> | null | undefined,
): Map<string, string> {
  const map = new Map<string, string>();
  if (!workouts) return map;
  for (const w of workouts) {
    for (const entry of w.entries) {
      const prev = map.get(entry.exerciseId);
      // Keep the latest startedAt per slug. ISO-8601 compares lexicographically.
      if (prev === undefined || w.startedAt > prev) {
        map.set(entry.exerciseId, w.startedAt);
      }
    }
  }
  return map;
}

// Comparator: more-recently-logged first, then never-logged in name order.
// `nameOf` resolves the display name for the alpha tiebreak so the tail of
// the list (exercises with no recent history) stays predictable.
export function compareByRecency(
  recency: ReadonlyMap<string, string>,
  nameOf: (slug: string) => string,
): (aSlug: string, bSlug: string) => number {
  return (aSlug, bSlug) => {
    const a = recency.get(aSlug);
    const b = recency.get(bSlug);
    if (a !== undefined && b !== undefined) {
      if (a !== b) return a > b ? -1 : 1; // newer startedAt first
      return nameOf(aSlug).localeCompare(nameOf(bSlug));
    }
    if (a !== undefined) return -1; // logged beats never-logged
    if (b !== undefined) return 1;
    return nameOf(aSlug).localeCompare(nameOf(bSlug));
  };
}
